import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, FileDown } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { StatusDot } from '@/components/ui/StatusDot'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { PlansService, type Plan } from '@/services/modules/PlansService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'

/** PESSOA FÍSICA → PF, PESSOA JURÍDICA → PJ (igual aos outros módulos). */
function shortPersonType(t: string | null | undefined): string {
  const v = (t ?? '').toUpperCase()
  if (v.includes('FÍSICA') || v.includes('FISICA')) return 'PF'
  if (v.includes('JURÍDICA') || v.includes('JURIDICA')) return 'PJ'
  return t ?? '—'
}

export function PlansListPage() {
  const navigate = useNavigate()
  const { can } = useAuthority()
  const canCreate = can('commercial.plans.create')
  const canEdit = can('commercial.plans.edit')
  const canDelete = can('commercial.plans.delete')
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({
    searchPlaceholder: 'Buscar plano',
    extraFields: [
      { key: 'website', label: 'Website', placeholder: 'Todos', options: [
        { value: 'true', label: 'Somente na LP' },
        { value: 'false', label: 'Fora da LP' },
      ] },
    ],
  })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(PlansService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<Plan | null>(null)

  const exp = useDataTableExport({
    filename: 'planos',
    service: PlansService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',          accessor: (p: Plan) => p.name },
      { header: 'Tipo',          accessor: (p: Plan) => shortPersonType(p.planType) },
      { header: 'Modalidade',    accessor: (p: Plan) => p.planTypeRelationship?.name ?? '' },
      { header: 'Preço (R$)',    accessor: (p: Plan) => p.price },
      { header: 'Titulares',     accessor: (p: Plan) => p.subscribersCount ?? 0 },
      { header: 'Vidas',         accessor: (p: Plan) => p.livesCount ?? 0 },
      { header: 'No website',    accessor: (p: Plan) => p.displayWebsite ? 'SIM' : 'NÃO' },
      { header: 'Status',        accessor: (p: Plan) => p.status },
    ],
  })

  /** Titulares que estão no plano hoje (ativos), com os dependentes de cada um. */
  async function exportSubscribers(plan: Plan) {
    try {
      const rows = await PlansService.subscribers(plan.id, true)
      if (rows.length === 0) {
        toast.error({ title: 'Nada a exportar', description: 'Este plano não possui titulares ativos.' })
        return
      }
      exportToXlsx(`associados-${plan.name}`, rows, [
        { header: 'Titular',             accessor: r => r.name },
        { header: 'CPF',                 accessor: r => r.cpf ?? '' },
        { header: 'E-mail',              accessor: r => r.email ?? '' },
        { header: 'Celular',             accessor: r => r.mobile ?? '' },
        { header: 'Empresa',             accessor: r => r.company ?? '' },
        { header: 'Carteirinha',         accessor: r => r.card ?? '' },
        { header: 'Dependentes ativos',  accessor: r => r.dependents },
        { header: 'Vidas',               accessor: r => 1 + r.dependents },
        { header: 'Status',              accessor: r => r.status },
      ])
    } catch (e) {
      toast.error({ title: 'Erro ao exportar', description: extractApiError(e).message })
    }
  }

  async function handleDelete() {
    if (!openDelete) return
    try {
      await PlansService.destroy(openDelete.id)
      toast.success({ title: 'Removido', description: 'Plano removido com sucesso.' })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<Plan>[] = [
    {
      key: 'name', header: 'Nome', sortable: true, alwaysVisible: true,
      render: p => (
        <div>
          <div className="font-medium text-gray-800">{p.name}</div>
          <div className="text-[11px] text-gray-500">{p.planTypeRelationship?.name ?? '—'}</div>
        </div>
      ),
    },
    { key: 'planType', header: 'Tipo', align: 'center', render: p => <span className="text-xs font-medium text-gray-600">{shortPersonType(p.planType)}</span>, className: 'w-16' },
    { key: 'price',    header: 'Preço', align: 'center', render: p => <span>{p.formatted?.price ?? `R$ ${p.price.toFixed(2).replace('.', ',')}`}</span>, className: 'w-28' },
    {
      key: 'subscribers', header: 'Titulares', align: 'center', className: 'w-24',
      render: p => <span className="text-gray-700">{p.subscribersCount ?? 0}</span>,
    },
    {
      // Vidas = titulares ativos + dependentes ativos deles.
      key: 'lives', header: 'Vidas', align: 'center', className: 'w-24',
      render: p => <span className="font-medium text-gray-800" title="Titulares ativos + dependentes ativos">{p.livesCount ?? 0}</span>,
    },
    {
      key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: p => (
        <StatusDot value={p.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Planos"
        subtitle="Catálogo de planos de benefícios"
        crumbs={[{ label: 'Comercial' }, { label: 'Planos' }]}
        actions={canCreate ? (
          <Button onClick={() => navigate('/plans/novo')} iconLeft={<Plus size={14} />}>
            Novo plano
          </Button>
        ) : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="plans.list"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(p) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/plans/${p.id}/editar`) }] : []),
          { key: 'export',  label: 'Exportar associados', icon: <FileDown size={14} />, onClick: () => exportSubscribers(p) },
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setOpenDelete(p) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover plano?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
